import type { AgentStatus } from '../../stores'

// Playful, Star-Trek-flavored copy for the live status line. daystrom sends
// only a semantic `activity` ("thinking", "subagent"); the personality lives
// here, entirely client-side. Richard Daystrom built the M-5 multitronic
// computer, so the assistant leans into that. Keep these short, tasteful, and
// non-technical — never leak a raw tool name.

const GENERAL_POOL = [
  'Consulting the ship computer…',
  'Computing, Captain…',
  'Running level-3 diagnostics…',
  'Realigning the sensor array…',
  'Beep bop boop…',
]

// Keyed by daystrom subagent name (see daystrom `SUBAGENTS`). An unknown
// subagent falls back to the general pool, so adding a subagent server-side
// never breaks the status line.
const SUBAGENT_POOLS: Record<string, string[]> = {
  // Traces where money went — external parties and recipients.
  external_accounts: [
    'Dispatching an away team to trace the funds…',
    'Hailing the recipients on subspace…',
    'Following the trail of latinum…',
    'Triangulating the transfer signatures…',
    'Scanning for external account signals…',
    'Cross-checking the recipient manifest…',
    'Pinging the destination accounts…',
    'Mapping the flow of funds across the quadrant…',
    'Identifying the parties on the other end…',
    'Tracking the transfers to their endpoints…',
    'Logging the counterparties in the ledger…',
    'Decrypting the wire signatures…',
    'Beep bop boop…',
  ],
  // Maps where in-person transactions happened.
  places_visited: [
    'Charting the star map…',
    'Plotting the coordinates…',
    'Laying in a course to each location…',
    'Tracing the landing party’s path…',
    'Marking waypoints on the viewscreen…',
    'Scanning the sector for ports of call…',
    'Geolocating each point of sale…',
    'Aligning the navigational beacons…',
    'Pinning the merchants on the grid…',
    'Calculating the travel between stops…',
    'Sweeping the surface for landing sites…',
    'Updating the log of ports visited…',
    'Beep bop boop…',
  ],
  // Heavy, slow reasoning on a hard question.
  deep_reasoning: [
    'Engaging the M-5 multitronic unit…',
    'Routing through the positronic core…',
    'Diverting power to the reasoning core…',
    'Letting the multitronic matrix deliberate…',
    'Running it through every logic circuit…',
    'Consulting the Daystrom Institute archives…',
    'Weighing every branch of the problem…',
    'Simulating the outcomes at warp…',
    'Cross-examining the evidence…',
    'Stress-testing each hypothesis…',
    'Reconciling the contradictions…',
    'Reasoning it through, step by step…',
    'Beep bop boop…',
  ],
}

// The pool a given status draws from. Exported for unit tests.
export function agentStatusPool(status: AgentStatus): string[] {
  if (status.subagent) {
    const pool = SUBAGENT_POOLS[status.subagent]
    if (pool) return pool
  }
  return GENERAL_POOL
}

// Pick one phrase from the status's pool. `index` is the caller's rotating
// counter, so the label cycles within its pool over time. Wraps with modulo,
// so any index is valid.
export function agentStatusLabel(status: AgentStatus, index: number): string {
  const pool = agentStatusPool(status)
  return pool[index % pool.length]
}
